/**
 * What each admin role may do — ARCHITECTURE.md §7.6's "a deposit-approver ≠ a
 * config-editor" made explicit, so the blast radius of one stolen credential is
 * bounded.
 *
 * **Shared, not mirrored.** The API enforces this table on every route via
 * `adminGuard`; the admin console reads the same table to decide which controls
 * to render. Those are two different jobs, and for a while they ran off two
 * hand-kept copies — until the server gained `media_asset.manage` and
 * `system.notice` for `operator` (commit 107ac1f) and the console's copy did
 * not. The result was silent in exactly the worst way: an operator opened the
 * Posters screen, the list loaded (the server allowed the read), and every
 * Edit/Replace/Delete control was simply absent, with nothing anywhere saying
 * why.
 *
 * This is what `@bingo/shared` is for — the things both sides must agree on
 * *semantically*, the way money arithmetic is. It is unlike `api/types.ts`'s
 * deliberately mirrored view shapes: those describe one server's wire format,
 * where a drift is a type error at the point of use. A drift here renders a
 * different console.
 *
 * Rendering is still only cosmetics. Every admin route carries
 * `adminGuard(permission)` and the server decides; this exists so a `finance`
 * admin is not shown a "Create round" button that answers 403, which teaches
 * people to click hopefully and ignore errors.
 */
export const PERMISSIONS = {
    superadmin: ['*'],
    finance: [
        'deposit.review',
        'deposit.approve',
        'deposit.reject',
        'withdrawal.review',
        'withdrawal.approve',
        'withdrawal.process',
        'withdrawal.reject',
        'ledger.read',
        'user.read',
        'user.manage',
    ],
    operator: [
        'round.create',
        'round.start',
        'round.cancel',
        'round.read',
        // Content, not money: the same "operator runs the day-to-day" scope that
        // already covers round management. `finance`/`auditor` don't touch this.
        'media_asset.manage',
        'system.notice',
        'user.read',
        'user.manage',
    ],
    auditor: ['ledger.read', 'audit.read', 'reconcile.run', 'round.read', 'user.read'],
};
/** Whether `role` holds `permission`. An unknown role holds nothing. */
export function can(role, permission) {
    if (!role)
        return false;
    const granted = PERMISSIONS[role] ?? [];
    return granted.includes('*') || granted.includes(permission);
}
//# sourceMappingURL=permissions.js.map